import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useFormContext } from 'react-hook-form';
import axios from 'axios';
import ValidationErrorMessage from './ValidationErrorMessage';
import TextInput from './TextInput';
import SelectList from './SelectList';
import countries from 'iso-3166-1/dist/iso-3166';

/**
 * Component for rendering address fields with postcode lookup.
 *
 * @component
 * @params see propTypes
 * @returns {JSX.Element}
 */
const Address = ({ formConfig, addressCountry, setAddressCountry }) => {
  /**
   * All the possible address field states.
   * @type {{SHOW_ALL_FIELDS: number, LOOKUP_DISABLED: number, LOOKUP_ENABLED: number, DISPLAY_ADDRESS_LIST: number}}
   */
  const addressFieldStates = {
    LOOKUP_DISABLED: 0,
    LOOKUP_ENABLED: 1,
    DISPLAY_ADDRESS_LIST: 2,
    SHOW_ALL_FIELDS: 3
  };

  /**
   * UK iso-3166-1 Country Code
   * @type {string}
   */
  const UK_COUNTRY_CODE = '826';

  /**
   * Deconstructs react hook form methods from parent form.
   */
  const {
    register,
    setValue,
    getValues,
    setError,
    formState: { errors },
    unregister
  } = useFormContext();

  /**
   * The default address component state.
   * @type {{addresses: Array, state: number}}
   */
  const defaultState = {
    state: addressFieldStates.LOOKUP_ENABLED,
    addresses: []
  };

  /**
   * A react state variable, addressFieldState
   */
  const [addressFieldState, setAddressFieldState] = useState(defaultState);

  /**
   * List of Countries
   * @type {Country[]}
   */
  const isoCountries = require('iso-3166-1').all();

  /**
   * The return value of the react hook form register function for the country select.
   *
   * The is done here so the react re
   * @type {UseFormRegisterReturn}
   */
  const countryRegister = register('HPP_BILLING_COUNTRY', {
    required: 'Select a country',
    value: addressCountry.numeric
  });

  /**
   * Show all address fields.
   */
  const showAllFields = e => {
    e.preventDefault();
    unregister('HPP_BILLING_POSTALCODE');

    setAddressFieldState(previousFieldState => {
      return { ...previousFieldState, state: addressFieldStates.SHOW_ALL_FIELDS, addresses: [] };
    });
  };

  /**
   * Sets the address in the select list in the address text inputs.
   *
   * @param event - The triggering event.
   */
  const useThisAddress = (event) => {
    event.preventDefault();

    const selectId = getValues('HPP_BILLING_ADDRESS_SELECT');
    const selected = addressFieldState.addresses.find(address => address.selectId === selectId);

    const building = selected.BUILDING_NAME || selected.BUILDING_NUMBER;

    setValue('HPP_BILLING_STREET1', building + ' ' + selected.THOROUGHFARE_NAME);
    setValue('HPP_BILLING_STREET2', selected.DEPENDENT_LOCALITY);
    setValue('HPP_BILLING_CITY', selected.POST_TOWN);
    setValue('HPP_BILLING_COUNTY', selected.LOCAL_CUSTODIAN_CODE_DESCRIPTION);
    setValue('HPP_BILLING_POSTALCODE', selected.POSTCODE);
    setValue('HPP_BILLING_ADDRESS_SELECT', '');

    setAddressFieldState(previousFieldState => {
      return { ...previousFieldState, state: addressFieldStates.SHOW_ALL_FIELDS };
    });
  };

  /**
   * Runs the postcode lookup search.
   *
   * @param event
   */
  const postCodeLookup = (event) => {
    event.preventDefault();

    const postCode = getValues('POSTALCODE_LOOKUP');

    if (!postCode) {
      setError('POSTALCODE_LOOKUP', {
        type: 'manual',
        message: 'Please enter a postcode'
      });
      return;
    }

    const settingsUrl = `${process.env.REACT_APP_HE_ECOM_API}/Address/PostcodeLookup/${postCode}`;

    axios.get(settingsUrl)
      .then(response => {
        const addresses = response.data;

        addresses.forEach((address, index) => {
          addresses[index].selectId = address.UPRN + index;
        });

        addresses.sort((a, b) => {
          return a.ADDRESS.localeCompare(b.ADDRESS, 'en', { numeric: true });
        });

        setAddressFieldState(previousFieldState => {
          return {
            ...previousFieldState,
            state: addressFieldStates.DISPLAY_ADDRESS_LIST,
            addresses: addresses
          };
        });
      })
      .catch((errors) => {
        if (errors.response.status === 404) {
          setError('POSTALCODE_LOOKUP', {
            type: 'manual',
            message: 'No addresses were returned for this Postcode, please try again or enter your address manually.'
          });
          return;
        }

        setError('POSTALCODE_LOOKUP', {
          type: 'manual',
          message: 'There was a problem with the postcode lookup. Please try again or enter your address manually.'
        });
      });
  };

  /**
   * Gets the post code lookup state based on country code.
   * @param countryCode
   * @returns {number}
   */
  const getStateByCountryCode = (countryCode) => {
    if (countryCode === UK_COUNTRY_CODE) {
      return addressFieldStates.LOOKUP_ENABLED;
    }

    return addressFieldStates.LOOKUP_DISABLED;
  };

  /**
   * Sets the default values of all address fields.
   */
  const setDefaultValues = () => {
    setValue('HPP_BILLING_STREET1', '');
    setValue('HPP_BILLING_STREET2', '');
    setValue('HPP_BILLING_STREET3', '');
    setValue('HPP_BILLING_CITY', '');
    setValue('HPP_BILLING_COUNTY', '');
    setValue('HPP_BILLING_POSTALCODE', '');
    setValue('POSTALCODE_LOOKUP', '');
  };

  /**
   * Sets the addressCountry state on change.
   * @param e
   */
  const onCountryChange = (e) => {
    const countryCode = e.target.value;

    const country = countries.find(country => country.numeric === e.target.value);

    setAddressCountry({
      numeric: country.numeric,
      alpha2: country.alpha2
    });

    setAddressFieldState(previousFieldState => {
      return { ...previousFieldState, state: getStateByCountryCode(countryCode) };
    });

    setDefaultValues();
  };

  /**
   * Render the html for the postcode lookup.
   *
   * @returns {JSX.Element}
   */
  const renderPostCodeLookup = () => {
    return (
      <>
        <TextInput
          label="Postcode"
          name="POSTALCODE_LOOKUP"
          validation={formConfig.Fields.HPP_BILLING_POSTALCODE}
          customValidation={{
            validate: {
              requires_selection: () => {
                if (addressFieldState.state === addressFieldStates.LOOKUP_ENABLED) return 'Please click find my address';
              }
            }
          }}
          register={register}
          errors={errors}>
          <>
            <button
              type="button"
              className="btn button button--primary"
              onClick={postCodeLookup}>
              Find my address (UK only)
            </button>
          </>
        </TextInput>
        <a
          href="#"
          className="align-self-start mt-2"
          onClick={showAllFields}>
          I&#39;d like to enter my address manually
        </a>
      </>

    );
  };

  /**
   * Renders the html for the address select.
   * @returns {JSX.Element}
   */
  const renderAddressSelect = () => {
    return (
      <div>
        <SelectList
          name="HPP_BILLING_ADDRESS_SELECT"
          label="Select an address"
          customValidation={{
            required: 'Please select an address',
            validate: {
              requires_selection: () => {
                return 'Please click use this address';
              }
            }
          }}
          options={addressFieldState.addresses}
          optionTextKey="ADDRESS"
          optionValueKey="selectId"
          displayError={false}
        />

        <div className="d-flex align-items-center mt-6">
          <button
            className="btn button button--primary"
            type="button"
            onClick={useThisAddress}>
            Use this address
          </button>
          <div>
            <a 
              className="ms-5"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setAddressFieldState(defaultState);
              }}>
              Cancel
            </a>
          </div>
        </div>
        {
          errors.HPP_BILLING_ADDRESS_SELECT &&
          <ValidationErrorMessage error={errors.HPP_BILLING_ADDRESS_SELECT}/>
        }
      </div>

    );
  };

  /**
   * Renders all the address fields.
   * @returns {JSX.Element}
   */
  const renderAllFields = () => {
    return (
      <div>
        <TextInput
          label="Address line 1"
          name="HPP_BILLING_STREET1"
          validation={formConfig.Fields.HPP_BILLING_STREET1}
          register={register}
          errors={errors}/>

        <TextInput
          label="Address line 2"
          name="HPP_BILLING_STREET2"
          validation={formConfig.Fields.HPP_BILLING_STREET2}
          register={register}
          errors={errors}/>

        <TextInput
          label="Address line 3"
          name="HPP_BILLING_STREET3"
          validation={formConfig.Fields.HPP_BILLING_STREET3}
          register={register}
          errors={errors}/>

        <TextInput
          label="Town / City"
          name="HPP_BILLING_CITY"
          validation={formConfig.Fields.HPP_BILLING_CITY}
          register={register}
          errors={errors}/>

        <TextInput
          label="County"
          name="HPP_BILLING_COUNTY"
          validation={formConfig.Fields.HPP_BILLING_COUNTY}
          register={register}
          errors={errors}/>

        <TextInput
          label="Postcode"
          name="HPP_BILLING_POSTALCODE"
          validation={formConfig.Fields.HPP_BILLING_POSTALCODE}
          register={register}
          errors={errors}/>
      </div>
    );
  };

  /**
   * Render the address based on the addressFieldState
   * @returns {Element}
   */
  const renderAddressFields = () => {
    switch (addressFieldState.state) {
      case addressFieldStates.LOOKUP_ENABLED:
        return renderPostCodeLookup();

      case addressFieldStates.DISPLAY_ADDRESS_LIST:
        return renderAddressSelect();

      default:
        return renderAllFields();
    }
  };

  return (
    <div className="d-flex flex-column">
      <label
        htmlFor="HPP_BILLING_COUNTRY"
        className="mt-8 fw-normal">
        Select a country
      </label>

      {
        errors.HPP_BILLING_COUNTRY &&
        <ValidationErrorMessage error={errors.HPP_BILLING_COUNTRY}/>
      }

      <div className="c-select-wrapper position-relative mt-2">
        <select
          id="HPP_BILLING_COUNTRY"
          className={`border-1 border-${errors.HPP_BILLING_COUNTRY ? 'rhubarb-triangle' : 'blue-anchor'} py-3 ps-2 pe-12 w-100 text-black`}
          name={countryRegister.name}
          onBlur={countryRegister.onBlur}
          ref={countryRegister.ref}
          onChange={(e) => {
            countryRegister.onChange(e);
            onCountryChange(e);
          }}>
          <option value="">
            Select...
          </option>
          {
            isoCountries.map(({ country, numeric }) =>
              <option
                value={numeric}
                key={numeric}>
                {country}
              </option>)
          }
        </select>
      </div>

      {renderAddressFields()}
    </div>
  );
};

Address.propTypes = {
  /**
   * The form settings returned from the settings api.
   */
  formConfig: PropTypes.object.isRequired,
  /**
   * The donation form addressCountry state variable.
   */
  addressCountry: PropTypes.object.isRequired,
  /**
   * The donation form setAddressCountry update state function.
   */
  setAddressCountry: PropTypes.func.isRequired
};

export default Address;
